import {
  ALERTIS_SIRET_SANS_TVA,
  ALERTIS_SIRET_AVEC_TVA,
  ALERTIS_TVA,
  ACTIVITE_PRINCIPALE_EXCEPTION,
  ALERTIS_1_COMPANY_NAME,
  ALERTIS_2_COMPANY_NAME,
  ALERTIS_1_DECLARATION_NUMBER,
  ALERTIS_2_DECLARATION_NUMBER,
  ALERTIS_1_TVA_LABEL,
  ALERTIS_2_TVA_LABEL,
  ALERTIS_2_TVA_RATE,
  ALERTIS_1_HEADER,
  ALERTIS_2_HEADER
} from './alertis-constants.js';

/**
 * Tout ce qu'un devis écrit sur la société Alertis qui l'émet, à partir de son
 * SIRET : nom, en-tête, déclaration d'activité, TVA. UNE SEULE fabrique, pour
 * la règle automatique (getAlertisSiretInfoSync) comme pour le sélecteur
 * ALERTIS 1 / ALERTIS 2 de l'éditeur : un devis ne peut ainsi pas porter
 * l'en-tête d'une société et le SIRET de l'autre.
 *
 * @param {string} siret - ALERTIS_SIRET_SANS_TVA (Alertis 1) ou ALERTIS_SIRET_AVEC_TVA (Alertis 2)
 * @returns {{ siret: string, tva: string|null, displayText: string, companyName: string,
 *   numeroDeclaration: string, tvaLabel: string, tvaRate: number,
 *   header: { companyName: string, companySubtitle: string } }}
 */
export function alertisSocieteParSiret(siret) {
  if (siret === ALERTIS_SIRET_SANS_TVA) {
    return {
      siret: ALERTIS_SIRET_SANS_TVA,
      tva: null,
      displayText: ALERTIS_SIRET_SANS_TVA,
      companyName: ALERTIS_1_COMPANY_NAME,
      numeroDeclaration: ALERTIS_1_DECLARATION_NUMBER,
      tvaLabel: ALERTIS_1_TVA_LABEL,
      tvaRate: 0,
      header: { ...ALERTIS_1_HEADER }
    };
  }
  return {
    siret: ALERTIS_SIRET_AVEC_TVA,
    tva: ALERTIS_TVA,
    displayText: `${ALERTIS_SIRET_AVEC_TVA} / TVA ${ALERTIS_TVA}`,
    companyName: ALERTIS_2_COMPANY_NAME,
    numeroDeclaration: ALERTIS_2_DECLARATION_NUMBER,
    tvaLabel: ALERTIS_2_TVA_LABEL,
    tvaRate: ALERTIS_2_TVA_RATE,
    header: { ...ALERTIS_2_HEADER }
  };
}

export const DATE_COUPURE_TVA_ALERTIS_2 = new Date('2026-10-10T22:00:00Z');

function dateDeCreation(quote) {
  const brute = quote.createdAt ?? quote.created_at;
  if (!brute) return null;
  const date = new Date(brute);
  return Number.isNaN(date.getTime()) ? null : date;
}

function creeAvantLaCoupure(quote) {
  const date = dateDeCreation(quote);
  return date !== null && date < DATE_COUPURE_TVA_ALERTIS_2;
}

function estAssujettiHorsOpco(quote) {
  const isProfessionnel = quote.client_type === 'professionnel' || quote.clientType === 'professionnel';
  const hasTva = quote.professionnel_tva === 'Oui' || quote.professionnelTva === 'Oui';
  return isProfessionnel && hasTva && (quote.financement || '') !== 'OPCO';
}

function activitePrincipaleDe(quote) {
  return quote.professionnel_activite_principale || quote.professionnelActivitePrincipale || null;
}

function ancienneRegleVersAlertis1(quote) {
  const activitePrincipale = activitePrincipaleDe(quote);
  return estAssujettiHorsOpco(quote) && Boolean(activitePrincipale) && activitePrincipale !== ACTIVITE_PRINCIPALE_EXCEPTION;
}

/**
 * Version synchrone qui retourne le SIRET/TVA sans faire d'appel API
 * Utilise uniquement les données stockées dans le quote
 * Shared between server and client — single source of truth
 *
 * Depuis le 11/10/2026, un client assujetti à la TVA reçoit un devis Alertis 2
 * comme les autres. Un devis créé avant cette date garde l'ancienne règle
 * (assujetti, hors OPCO, activité connue et différente de 84.11Z : Alertis 1),
 * pour ne pas changer de société à la relecture ; un devis sans date de
 * création (en cours de saisie) suit la règle nouvelle.
 * @param {Object} quote - L'objet quote avec les informations du client
 * @returns {Object} - Objet avec { siret, tva, displayText, companyName, numeroDeclaration, tvaLabel, tvaRate }
 */
export function getAlertisSiretInfoSync(quote) {
  const versAlertis1 = creeAvantLaCoupure(quote) && ancienneRegleVersAlertis1(quote);
  return alertisSocieteParSiret(versAlertis1 ? ALERTIS_SIRET_SANS_TVA : ALERTIS_SIRET_AVEC_TVA);
}

/**
 * Le taux de TVA quand il est déjà certain, `null` sinon : pour un devis créé
 * avant le 11/10/2026, un professionnel assujetti, hors OPCO, dont l'activité
 * principale n'est pas encore connue peut relever d'une société comme de
 * l'autre, donc d'un taux à 0 ou à 20 %. Depuis la coupure, le taux est
 * toujours certain.
 */
export function tvaRateCertain(quote) {
  const q = quote || {};
  const incertain = creeAvantLaCoupure(q) && estAssujettiHorsOpco(q) && !activitePrincipaleDe(q);
  return incertain ? null : getAlertisSiretInfoSync(q).tvaRate;
}

/**
 * Calcule le montant TTC à partir du montant HT et des données du quote
 * @param {number} amountHT - Montant hors taxes
 * @param {Object} quote - L'objet quote avec les informations du client
 * @returns {number} - Montant TTC
 */
export function calculateAmountTTC(amountHT, quote) {
  if (!amountHT || !quote) return amountHT || 0;
  const { tvaRate } = getAlertisSiretInfoSync(quote);
  return amountHT * (1 + tvaRate);
}

function parseCustomEditorData(value) {
  if (value == null) return null;
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * La société Alertis qui émet le devis. Le SIRET choisi dans l'éditeur (bouton
 * ALERTIS 1 / ALERTIS 2, enregistré dans custom_editor_data.organisateur.siret)
 * l'emporte sur la règle automatique : l'en-tête, la signature, le pied de page
 * et l'entité des inscriptions suivent ainsi le choix du commercial.
 *
 * @param {Object} quote - Le devis (Prisma camelCase ou formaté snake_case)
 * @returns {ReturnType<typeof alertisSocieteParSiret>}
 */
export function alertisSocieteDuDevis(quote) {
  const custom = parseCustomEditorData(quote?.custom_editor_data ?? quote?.customEditorData);
  const siretChoisi = custom?.organisateur?.siret;
  if (siretChoisi === ALERTIS_SIRET_SANS_TVA || siretChoisi === ALERTIS_SIRET_AVEC_TVA) {
    return alertisSocieteParSiret(siretChoisi);
  }
  return getAlertisSiretInfoSync(quote);
}

/**
 * Retourne les montants (HT, taux TVA, TTC) d'un devis, en tirant d'abord
 * de customEditorData (source du PDF) puis en fallback sur les règles
 * automatiques (getAlertisSiretInfoSync + quote.quoteAmount).
 *
 * Cette fonction garantit la cohérence email ↔ PDF : si le commercial a
 * basculé manuellement le SIRET/TVA via l'éditeur, l'email reflète ce choix.
 *
 * @param {Object} quote - L'objet quote (Prisma ou formatté)
 * @returns {{ totalHT: number, tvaRate: number, totalTTC: number }}
 */
export function getQuoteAmounts(quote) {
  if (!quote) return { totalHT: 0, tvaRate: 0, totalTTC: 0 };

  const rawCustom = quote.customEditorData != null ? quote.customEditorData : quote.custom_editor_data;
  const customData = parseCustomEditorData(rawCustom);
  const customPrix = customData?.prix || null;

  const hasCustomTvaRate = customPrix && customPrix.tvaRate !== undefined && customPrix.tvaRate !== null;
  const tvaRate = hasCustomTvaRate ? Number(customPrix.tvaRate) : getAlertisSiretInfoSync(quote).tvaRate;

  let totalHT = 0;
  if (customPrix && Array.isArray(customPrix.lignes) && customPrix.lignes.length > 0) {
    totalHT = customPrix.lignes.reduce((sum, ligne) => {
      const q = parseFloat(ligne?.quantite) || 0;
      const pu = parseFloat(ligne?.prixUnitaire) || 0;
      return sum + q * pu;
    }, 0);
  }
  if (!totalHT) {
    const fallback = quote.quoteAmount != null ? quote.quoteAmount : quote.quote_amount;
    totalHT = Number(fallback) || 0;
  }

  const totalTTC = totalHT * (1 + (Number.isFinite(tvaRate) ? tvaRate : 0));
  return { totalHT, tvaRate: Number.isFinite(tvaRate) ? tvaRate : 0, totalTTC };
}
